import React from 'react';
import { TiGift, TiHomeOutline, TiThLargeOutline, TiUser, TiWarningOutline, TiDownloadOutline } from 'react-icons/ti';
import {
  MdAssignment,
  MdAttachMoney,
  MdCreditCard,
  MdDateRange,
  MdEventNote,
  MdRateReview,
  MdSend,
  MdThumbUp,
} from 'react-icons/md';
import { GoInbox, GoOrganization, GoQuestion, GoVersions, GoPackage } from 'react-icons/go';
import { GiReceiveMoney } from 'react-icons/gi';
import { FiSearch, FiTag } from 'react-icons/fi';
import { RiStackshareLine } from 'react-icons/ri';
import { BsCardList, BsImage } from 'react-icons/bs';
import * as PATHS from './urlConstants';
import { ORDER_LIST_SIDEBAR_KEY, ORDER_MANAGEMENT_SIDEBAR_KEY } from './commonConstants';
import { PERMISSION_ACTIONS, PERMISSION_SUBJECTS } from './userPermissions';

/**
 *
 * 사이드바에서 사용될 사이드바 아이템 리스트
 *
 */
export const SIDEBAR_ITEM_LIST = [
  /**
   *  홈
   */
  {
    icon: <TiHomeOutline />,
    text: '홈',
    key: 'home',
    path: PATHS.HOME_URL_PATH,
  },
  /**
   *  계정 관리
   */
  {
    icon: <TiUser />,
    text: '계정 관리',
    key: 'account',
    subMenu: [
      {
        text: '판매자',
        path: PATHS.ACCOUNT_SELLER_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.SELLER, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '유저',
        path: PATHS.ACCOUNT_USER_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.USER, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '유저랭킹(구매금액,횟수)',
        path: PATHS.ACCOUNT_USER_RANK_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.USER_RANK, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '관리자',
        path: PATHS.ACCOUNT_ADMIN_LIST_URL_PATH,
        shouldMasterLevel: true,
      },
    ],
  },
  /**
   *  브랜드 관리
   */
  {
    icon: <GoOrganization />,
    text: '브랜드 관리',
    key: 'store',
    subMenu: [
      {
        text: '입점 승인',
        path: PATHS.BRAND_JOIN_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.STORE, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '브랜드 리스트',
        path: PATHS.BRAND_ALL_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.STORE, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '브랜드 태그 검수 리스트',
        path: PATHS.BRAND_TAG_REQUEST_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.STORE, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '브랜드 티어 관리',
        path: PATHS.BRAND_TIER_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.STORE_TIER,
          actions: [PERMISSION_ACTIONS.READLIST, PERMISSION_ACTIONS.UPDATE],
        },
      },
      {
        text: '브랜드 상단 노출 관리',
        path: PATHS.BRAND_TOP_EXPOSURE_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.STORE_EXPOSE, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '브랜드 노출 순서 관리',
        path: PATHS.BRAND_EXPOSURE_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.STORE_EXPOSE, actions: [PERMISSION_ACTIONS.READLIST] },
      },
    ],
  },
  /**
   *  상품 관리
   */
  {
    icon: <TiGift />,
    text: '상품 관리',
    key: 'product',
    subMenu: [
      {
        text: '상품 전체 리스트',
        path: PATHS.PRODUCT_ALL_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.PRODUCT, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '상품 승인 리스트',
        path: PATHS.PRODUCT_APPROVAL_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.PRODUCT, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '상품 금지 리스트',
        path: PATHS.PRODUCT_BAN_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.PRODUCT, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '상품 속성 검수 리스트',
        path: PATHS.PRODUCT_PROPERTY_REQUEST_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.PRODUCT, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '상품 등록',
        path: PATHS.PRODUCT_CREATE_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.PRODUCT, actions: [PERMISSION_ACTIONS.CREATE] },
      },
    ],
  },
  /**
   *  주문 내역
   */
  {
    icon: <BsCardList />,
    text: '주문 내역',
    key: ORDER_LIST_SIDEBAR_KEY,
    subMenu: [
      {
        text: '주문/배송 내역 리스트',
        path: PATHS.ORDER_ALL_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.ORDER, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '취소 내역 리스트',
        path: PATHS.ORDER_CANCEL_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.ORDER, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '반품 내역 리스트',
        path: PATHS.ORDER_RETURN_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.ORDER, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '교환 내역 리스트',
        path: PATHS.ORDER_EXCHANGE_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.ORDER, actions: [PERMISSION_ACTIONS.READLIST] },
      },
    ],
  },
  /**
   *  주문 관리
   */
  {
    icon: <MdAssignment />,
    text: '주문 관리',
    key: ORDER_MANAGEMENT_SIDEBAR_KEY,
    subMenu: [
      {
        text: '주문/배송 관리',
        path: PATHS.ORDER_MANAGEMENT_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.ORDER, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '취소 관리',
        path: PATHS.ORDER_CANCEL_MANAGEMENT_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.ORDER, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '반품 관리',
        path: PATHS.ORDER_RETURN_MANAGEMENT_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.ORDER, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '교환 관리',
        path: PATHS.ORDER_EXCHANGE_MANAGEMENT_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.ORDER, actions: [PERMISSION_ACTIONS.READLIST] },
      },
    ],
  },
  /**
   *  멀티채널 관리
   */
  {
    icon: <GoPackage />,
    text: '멀티채널 관리',
    key: 'multiChannel',
    subMenu: [
      {
        text: '멀티채널 주문 관리',
        path: PATHS.MULTI_CHANNEL_ORDER_MANAGEMENT_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.MULTI_CHANNEL_ORDER_ITEM,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '멀티채널 주문 생성',
        path: PATHS.MULTI_CHANNEL_ORDER_CREATE_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.MULTI_CHANNEL_ORDER_ITEM, actions: [PERMISSION_ACTIONS.CREATE] },
      },
    ],
  },
  /**
   *  정산 관리
   */
  {
    icon: <MdAttachMoney />,
    text: '정산 관리',
    key: 'balance',
    subMenu: [
      {
        text: '브랜드별 정산 타입 목록',
        path: PATHS.BALANCE_BRAND_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.STORE_BALANCE, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '정산 대기목록',
        path: PATHS.BALANCE_WAITING_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.BALANCE, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '정산 완료목록',
        path: PATHS.BALANCE_DONE_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.BALANCE, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '정산 보류목록',
        path: PATHS.BALANCE_HOLD_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.BALANCE, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '정산 기준일 변경',
        path: PATHS.BALANCE_ADJUSTMENT_BASE_DATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.SETTLEMENT_DATE_ADJUSTMENT,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '정산 완료일 변경',
        path: PATHS.BALANCE_ADJUSTMENT_COMPLETE_DATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.SETTLEMENT_COMPLETE_DATE_ADJUSTMENT,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
    ],
  },
  /**
   *  결제 관리
   */
  {
    icon: <MdCreditCard />,
    text: '결제 관리',
    key: 'payment',
    subMenu: [
      {
        text: '결제 설정',
        path: PATHS.PAYMENT_PG_ROUTE_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.PAYMENT_PG_ROUTE, actions: [PERMISSION_ACTIONS.READ] },
      },
      {
        text: 'PG 수수료 관리',
        path: PATHS.PAYMENT_PG_FEE_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.PAYMENT_PG_FEE, actions: [PERMISSION_ACTIONS.READ] },
      },
    ],
  },
  /**
   *  태그 관리
   */
  {
    icon: <FiTag />,
    text: '태그 관리',
    key: 'tag',
    subMenu: [
      {
        text: '태그 등록',
        path: PATHS.TAG_CREATE_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.TAG, actions: [PERMISSION_ACTIONS.CREATE] },
      },
      {
        text: '태그 리스트',
        path: PATHS.TAG_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.TAG, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '태그 그룹 등록',
        path: PATHS.TAG_GROUP_CREATE_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.TAG_GROUP, actions: [PERMISSION_ACTIONS.CREATE] },
      },
      {
        text: '태그 그룹 리스트',
        path: PATHS.TAG_GROUP_LIST_URL_PATH,
        permissions: { subject: PERMISSION_SUBJECTS.TAG_GROUP, actions: [PERMISSION_ACTIONS.READLIST] },
      },
      {
        text: '태그 메인 탭 관리',
        path: PATHS.TAG_TAB_MAIN_MANAGEMENT_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.TAG_TAB_MAIN,
          actions: [PERMISSION_ACTIONS.READ, PERMISSION_ACTIONS.UPDATE],
        },
      },
      {
        text: '태그 트렌드 키워드 관리',
        path: PATHS.TAG_TREND_KEYWORD_MANAGEMENT_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.TAG_TREND_KEYWORD,
          actions: [PERMISSION_ACTIONS.READ, PERMISSION_ACTIONS.UPDATE],
        },
      },
    ],
  },
  /**
   *  페이지 관리
   */
  {
    icon: <TiThLargeOutline />,
    text: '페이지 관리',
    key: 'page',
    subMenu: [
      {
        text: '홈 탭 등록 및 편집',
        path: PATHS.MAIN_TAB_MANAGEMENT_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.MENU,
          actions: [PERMISSION_ACTIONS.READLIST, PERMISSION_ACTIONS.UPDATE, PERMISSION_ACTIONS.CREATE],
        },
      },
      {
        text: '메인 페이지 관리',
        path: PATHS.MAIN_PAGE_MANAGEMENT_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.MAIN_PAGE,
          actions: [PERMISSION_ACTIONS.READLIST, PERMISSION_ACTIONS.UPDATE],
        },
      },
      {
        text: '서브 페이지 리스트',
        path: PATHS.SUB_PAGE_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.SUB_PAGE,
          actions: [
            PERMISSION_ACTIONS.READLIST,
            PERMISSION_ACTIONS.READ,
            PERMISSION_ACTIONS.CREATE,
            PERMISSION_ACTIONS.UPDATE,
            PERMISSION_ACTIONS.DELETE,
          ],
        },
      },
      {
        text: '위젯 전체 리스트',
        path: PATHS.WIDGET_ALL_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.WIDGET,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '위젯 등록',
        path: PATHS.WIDGET_CREATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.WIDGET,
          actions: [PERMISSION_ACTIONS.CREATE],
        },
      },
      {
        text: '메뉴 등록 및 편집',
        path: PATHS.WIDGET_HOME_MENU_CREATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.MENU,
          actions: [PERMISSION_ACTIONS.READLIST, PERMISSION_ACTIONS.UPDATE],
        },
      },
    ],
  },
  /**
   *  카테고리 관리
   */
  {
    icon: <RiStackshareLine />,
    text: '카테고리 관리',
    key: 'categories',
    subMenu: [
      {
        text: '카테고리 생성 관리',
        path: PATHS.CATEGORY_CREATE_MANAGEMENT_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.PRODUCT_CATEGORY,
          actions: [
            PERMISSION_ACTIONS.READLIST,
            PERMISSION_ACTIONS.READ,
            PERMISSION_ACTIONS.CREATE,
            PERMISSION_ACTIONS.UPDATE,
            PERMISSION_ACTIONS.DELETE,
          ],
        },
      },
      {
        text: '카테고리 전시 관리',
        path: PATHS.CATEGORY_EXHIBIT_MANAGEMENT_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.PRODUCT_CATEGORY_EXHIBITION,
          actions: [PERMISSION_ACTIONS.READLIST, PERMISSION_ACTIONS.UPDATE],
        },
      },
    ],
  },
  /**
   *  원격 이미지 관리
   */
  {
    icon: <BsImage />,
    text: '원격 이미지 관리',
    key: 'remoteImage',
    subMenu: [
      {
        text: '스플래시 이미지',
        path: PATHS.REMOTE_SPLASH_IMAGE_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.REMOTE_SPLASH_IMAGE,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '레퍼럴 이미지',
        path: PATHS.REMOTE_REFERRAL_IMAGE_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.REMOTE_REFERRAL_IMAGE,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
    ],
  },
  /**
   *  프로모션 관리
   *
   *  연간 프로모션 계획(스케줄 타임라인). 기획전은 이 계획의 산출물이라
   *  기획전 관리와 별개 메뉴로 두고, 계획이 실행보다 앞이므로 위에 배치한다.
   */
  {
    icon: <MdDateRange />,
    text: '프로모션 관리',
    key: 'promotion',
    subMenu: [
      /**
       * 표면 용어는 `프로모션` 으로 통일한다 — `스케줄` 은 보는 방식(달력)이지 대상이 아니다.
       * 헤더 숏컷 목록이 전부 "도메인 명사 + 번호"(상품번호·기획전번호·쿠폰번호)라
       * 여기 들어갈 이름도 `프로모션번호` 지 `스케줄번호` 가 아니다.
       * 내부(엔티티·경로·권한 subject)는 그대로 둔다 — 경로를 바꾸면 dev 북마크만 깨진다.
       */
      {
        text: '프로모션 타임라인',
        path: PATHS.PROMOTION_SCHEDULE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.PROMOTION_SCHEDULE,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '프로모션 리스트',
        path: PATHS.PROMOTION_SCHEDULE_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.PROMOTION_SCHEDULE,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
    ],
  },
  /**
   *  기획전 관리
   */
  {
    icon: <MdEventNote />,
    text: '기획전 관리',
    key: 'event',
    subMenu: [
      {
        text: '진행중인 기획전',
        path: PATHS.EVENT_IN_PROGRESS_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.EVENT,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '전체 기획전',
        path: PATHS.EVENT_ALL_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.EVENT,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '기획전 등록',
        path: PATHS.EVENT_CREATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.EVENT,
          actions: [PERMISSION_ACTIONS.CREATE],
        },
      },
    ],
  },
  /**
   *  쿠폰 관리
   */
  {
    icon: <GoInbox />,
    text: '쿠폰 관리',
    key: 'coupon',
    subMenu: [
      {
        text: '쿠폰 리스트',
        path: PATHS.COUPON_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.COUPON,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '쿠폰 생성',
        path: PATHS.COUPON_CREATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.COUPON,
          actions: [PERMISSION_ACTIONS.CREATE],
        },
      },
      {
        text: '쿠폰 발급',
        path: PATHS.COUPON_GENERATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.COUPON,
          actions: [PERMISSION_ACTIONS.CREATE],
        },
      },
      {
        text: '쿠폰 발급 내역 리스트',
        path: PATHS.COUPON_ISSUE_HISTORY_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.COUPON,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
    ],
  },
  /**
   *  알림톡 발송
   */
  {
    icon: <MdSend />,
    text: '알림톡 발송',
    key: 'alimtalk',
    subMenu: [
      {
        text: '발송 등록',
        path: PATHS.ALIMTALK_SEND_CREATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.ALIMTALK_SEND_SCHEDULE,
          actions: [PERMISSION_ACTIONS.CREATE],
        },
      },
    ],
  },
  /**
   *  포인트 관리
   */
  {
    icon: <GiReceiveMoney />,
    text: '포인트 관리',
    key: 'point',
    subMenu: [
      {
        text: '포인트 내역 리스트',
        path: PATHS.POINT_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.POINT,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '포인트 발급',
        path: PATHS.POINT_GENERATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.POINT,
          actions: [PERMISSION_ACTIONS.CREATE],
        },
      },
      {
        text: '포인트 발급 내역 리스트',
        path: PATHS.POINT_ISSUE_HISTORY_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.POINT,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '포인트 랭킹',
        path: PATHS.POINT_RANKING_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.POINT,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '레퍼럴 발급 현황',
        path: PATHS.POINT_REFERRAL_ISSUE_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.POINT,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '포인트 사유 리스트',
        path: PATHS.POINT_CAUSE_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.POINT,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
    ],
  },
  /**
   *  공지사항 관리
   */
  {
    icon: <TiWarningOutline />,
    text: '공지사항 관리',
    key: 'notice',
    subMenu: [
      {
        text: '상점 공지사항 리스트',
        path: PATHS.NOTICE_STORE_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.STORE_NOTICE,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '상점 공지사항 등록',
        path: PATHS.NOTICE_STORE_CREATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.STORE_NOTICE,
          actions: [PERMISSION_ACTIONS.CREATE],
        },
      },
      {
        text: '상품 공지사항 리스트',
        path: PATHS.NOTICE_PRODUCT_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.PRODUCT_NOTICE,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
    ],
  },
  /**
   *  검색 관리 (AMZUWEB-694 에서 「검색 키워드 관리」 → 「검색 관리」 로 이름만 바꿈 · key 는 권한·경로와 묶여 있어 유지)
   */
  {
    icon: <FiSearch />,
    text: '검색 관리',
    key: 'searchKeyword',
    subMenu: [
      {
        text: '검색 키워드 리스트',
        path: PATHS.SEARCH_WORD_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.SEARCH_KEYWORD,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '검색 키워드 등록',
        path: PATHS.SEARCH_WORD_CREATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.SEARCH_KEYWORD,
          actions: [PERMISSION_ACTIONS.CREATE],
        },
      },
      {
        text: '검색 사전',
        path: PATHS.SEARCH_DICTIONARY_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.SEARCH_DICTIONARY,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
    ],
  },
  /**
   *  문의 관리
   */
  {
    icon: <GoQuestion />,
    text: '문의 관리',
    key: 'cs',
    subMenu: [
      {
        text: '1:1 문의 리스트',
        path: PATHS.CS_INQUIRY_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.ONE_ON_ONE_INQUIRY,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '상품 문의 리스트',
        path: PATHS.CS_QNA_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.PRODUCT_INQUIRY,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
    ],
  },
  /**
   *  후기 관리
   */
  {
    icon: <MdRateReview />,
    text: '후기 관리',
    key: 'review',
    subMenu: [
      {
        text: '상품 후기 리스트',
        path: PATHS.CS_REVIEW_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.REVIEW,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '상품 후기 등록',
        path: PATHS.CS_REVIEW_CREATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.REVIEW,
          actions: [PERMISSION_ACTIONS.CREATE],
        },
      },
    ],
  },
  /**
   *  버전 관리
   */
  {
    icon: <GoVersions />,
    text: '버전 관리',
    key: 'version',
    subMenu: [
      {
        text: '버전 전체 리스트',
        path: PATHS.VERSION_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.VERSION,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '버전 등록',
        path: PATHS.VERSION_CREATE_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.VERSION,
          actions: [PERMISSION_ACTIONS.CREATE],
        },
      },
    ],
  },
  /**
   *  추천 상품 관리
   */
  {
    icon: <MdThumbUp />,
    text: '추천 관리',
    key: 'recommend',
    subMenu: [
      {
        text: '상품 추천 리스트',
        path: PATHS.RECOMMEND_PRODUCT_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.RECOMMEND_PRODUCT,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '상품 후기 추천 리스트',
        path: PATHS.RECOMMEND_REVIEW_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.RECOMMEND_PRODUCT_REVIEW,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '태그 추천 리스트',
        path: PATHS.RECOMMEND_TAG_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.RECOMMEND_TAG,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '검색 키워드 추천 리스트',
        path: PATHS.RECOMMEND_SEARCH_WORD_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.RECOMMEND_SEARCH,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '기획전 추천 리스트',
        path: PATHS.RECOMMEND_EVENT_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.RECOMMEND_EVENT,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
      {
        text: '브랜드 추천 리스트',
        path: PATHS.RECOMMEND_STORE_LIST_URL_PATH,
        permissions: {
          subject: PERMISSION_SUBJECTS.RECOMMEND_STORE,
          actions: [PERMISSION_ACTIONS.READLIST],
        },
      },
    ],
  },
  {
    icon: <TiDownloadOutline />,
    text: '응모하기 관리',
    key: 'luckydraw',
    subMenu: [
      {
        text: '응모내역 다운로드',
        path: PATHS.LUCKY_DRAW_URL_PATH,
      },
    ],
  },
];
